import { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, authHeader } from "../lib/api";
import { useGame } from "../context/GameContext";
import { useCompositeRecorder } from "../hooks/useCompositeRecorder";
import { RECORD_COMPOSITE_LIVE, COMPOSITE_RECORDER_BITRATES } from "../lib/videoCapture";
import {
  CAMERA_GUARD_MESSAGES,
  assertLiveAvTracks,
  ensureCameraImageUsable,
  stopMediaStream,
} from "../lib/cameraGuard";
import {
  playGameSample,
  playGameSampleWithEnd,
  preloadAudioSamples,
  stopActiveStoppableSample,
  unlockAudioContext,
} from "../lib/playAudio";
import TrapDoor from "../components/TrapDoor";
import BackgroundOrbs from "../components/BackgroundOrbs";
import CandidateSilhouetteGuide from "../components/CandidateSilhouetteGuide";
import { Circle, Loader2, Timer } from "lucide-react";
import "../play.css";

const INITIAL_WAGERS = [0, 0, 0, 0];
const FIRST_ANSWER_DELAY = 2000;       // délai avant la 1ère proposition à chaque question
const ANSWER_REVEAL_INTERVAL = 3000;  // 3s between each answer slot appearing
const QUESTION_REVEAL_DELAY = 1000;   // ms after answers shown before question appears
const WAGER_SECONDS = 45;              // visible countdown for placing wagers
const CHRONO_AUDIO_SRC = `${process.env.PUBLIC_URL || ""}/audio/Moneydrop_Chrono45.mp3`;
const PUSH_AUDIO_SRC = `${process.env.PUBLIC_URL || ""}/audio/push.mp3`;
const TRAP_OPEN_AUDIO_SRC = `${process.env.PUBLIC_URL || ""}/audio/Open.mp3`;
const TRAP_OPEN_GAIN = 5.0;            // Open est enregistré plus bas que push / valid
const VALID_AUDIO_SRC = `${process.env.PUBLIC_URL || ""}/audio/Moneydrop_valid.mp3`;
const PRE_TRAP_VALID_DELAY_MS = 2000;  // après la fin du fichier chrono, avant le son « valid »
const CHRONO_HIDE_DELAY_MS = 2000;     // délai après 00 avant le fondu
const CHRONO_FADEOUT_MS = 2000;        // durée du fondu de disparition
const ALARME_AUDIO_SRC = `${process.env.PUBLIC_URL || ""}/audio/alarme.mp3`;
const DROP_INTERVAL = 7000;            // ms entre la vague 2+1 et la dernière trappe
const TRAP_OPEN_SOUND_STAGGER_MS = 130; // décalage entre 2 Open joués quasi en même temps
const TRAP_REVEAL_WAVE_COUNT = 2;      // vague 1 (2 trappes) + vague 2 (1 trappe)
const TRAP_DROP_ANIM_MS = 700;         // aligné sur TrapDoor transition-all duration-700
const CORRECT_HIGHLIGHT_MS = 5000;     // ms on white border before next question
const CHANCE_NOTICE_MS = 4000;         // how long to show "Nouvelle chance" / "Dernière chance"
const END_REDIRECT_DELAY = 2000;

const shuffle = (items) => {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

/** Si le candidat a faux : la 3e trappe ouverte (dernière mauvaise) doit avoir une mise. */
const buildWrongRevealOrder = (wrongIndices, wagers) => {
  const withMoney = wrongIndices.filter((i) => wagers[i] > 0);
  if (withMoney.length === 0) return shuffle([...wrongIndices]);

  const lastDoor = withMoney.reduce((best, i) => (wagers[i] > wagers[best] ? i : best), withMoney[0]);
  const others = wrongIndices.filter((i) => i !== lastDoor);
  return [...shuffle(others), lastDoor];
};

/** Ouvre 2 trappes ensemble puis la 3e (ordre des mauvaises réponses inchangé). */
const buildTrapRevealWaves = (wrongOrder) => {
  if (wrongOrder.length <= 1) return wrongOrder.map((door) => [door]);
  if (wrongOrder.length === 2) return [[wrongOrder[0], wrongOrder[1]]];
  return [[wrongOrder[0], wrongOrder[1]], [wrongOrder[2]]];
};

const formatMoney = (n) => new Intl.NumberFormat("fr-FR").format(n) + " €";

// Game phases:
//   "intro-answers" : trap doors appear one by one, question hidden
//   "intro-question": question text revealed
//   "thinking"      : reveal_time_seconds countdown (NO visible timer, wagering disabled)
//   "wager"         : WAGER_SECONDS visible countdown, wagering enabled
//   "dropping"      : révélation des trappes en vagues (2 puis 1)
//   "chance-notice" : overlay showing "Nouvelle chance" or "Dernière chance"

const isPageReload = () => {
  if (typeof performance === "undefined") return false;
  const nav = performance.getEntriesByType?.("navigation")?.[0];
  return nav?.type === "reload";
};

const Play = () => {
  const { token, setSession, streamRef, setCompositeVideoBlob } = useGame();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const playScreenRef = useRef(null);
  const flashWagerRef = useRef({ active: false, startedAt: 0 });
  const compositeRevRef = useRef(0);
  const compositeLiveRef = useRef({ phase: "intro-answers", wagerSecondsLeft: WAGER_SECONDS, wagerTimerShown: false });
  const dropTimelineRef = useRef(null);
  const playScreenCallbackRef = useCallback((node) => {
    playScreenRef.current = node;
  }, []);
  const timersRef = useRef([]);
  const sessionIdRef = useRef(null);
  const wagerDeadlineRef = useRef(null);
  const wagerLockedRef = useRef(false);
  const trapSequenceScheduledRef = useRef(false);
  const pendingTrapRef = useRef(null);
  const wagerTimerFadeScheduledRef = useRef(false);
  const wagerTimerHideTimerRef = useRef(null);
  const wagerTimerFadeTimerRef = useRef(null);
  const chronoEndedAtRef = useRef(null);
  const questionGenerationRef = useRef(0);
  const lockWagerRef = useRef(() => {});

  const [starting, setStarting] = useState(true);
  const [recordingBitrates, setRecordingBitrates] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [bank, setBank] = useState(100000);
  const [wagers, setWagers] = useState(INITIAL_WAGERS);
  const [activeSlot, setActiveSlot] = useState(null);
  const [phase, setPhase] = useState("intro-answers");
  const [visibleAnswerCount, setVisibleAnswerCount] = useState(0);
  const [wagerSecondsLeft, setWagerSecondsLeft] = useState(WAGER_SECONDS);
  const [wagerTimerShown, setWagerTimerShown] = useState(false);
  const [wagerTimerFading, setWagerTimerFading] = useState(false);
  const [wagerTimerFadeStartedAt, setWagerTimerFadeStartedAt] = useState(0);
  const [flashWagerStart, setFlashWagerStart] = useState(false);
  const [correctIdx, setCorrectIdx] = useState(null);
  const [revealedDoors, setRevealedDoors] = useState([]); // wrong doors dropped
  const [correctHighlighted, setCorrectHighlighted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [wagersLocked, setWagersLocked] = useState(false);
  const [chanceNotice, setChanceNotice] = useState(null); // null | "new_chance" | "last_chance"

  const currentQ = questions[currentIdx];
  const showQuestionText = !starting && currentQ && phase !== "intro-answers";
  const liveTotalWager = wagers.reduce((a, b) => a + b, 0);
  const liveRemaining = bank - liveTotalWager;

  useEffect(() => {
    compositeLiveRef.current = {
      phase,
      chanceNotice,
      wagerSecondsLeft,
      wagerTimerShown,
      wagerTimerFading,
      wagerTimerFadeStartedAt,
      questionText: showQuestionText ? currentQ?.text ?? "" : "",
      showQuestion: showQuestionText && phase !== "chance-notice",
      questionIndex: currentIdx,
      questionCount: questions.length,
      bank,
      answers: currentQ?.answers ?? [],
      wagers,
      visibleAnswerCount,
      revealedDoors,
      correctIndex: correctIdx,
      correctHighlighted,
      totalWager: liveTotalWager,
      remaining: liveRemaining,
      showTotals: phase === "wager" && (wagerTimerShown || wagerTimerFading),
    };
  }, [
    phase,
    chanceNotice,
    wagerSecondsLeft,
    wagerTimerShown,
    wagerTimerFading,
    wagerTimerFadeStartedAt,
    showQuestionText,
    currentQ?.text,
    currentQ?.answers,
    currentIdx,
    questions.length,
    bank,
    wagers,
    visibleAnswerCount,
    revealedDoors,
    correctIdx,
    correctHighlighted,
    liveTotalWager,
    liveRemaining,
  ]);

  const clearWagerTimerDisplayTimers = useCallback(() => {
    if (wagerTimerHideTimerRef.current) {
      clearTimeout(wagerTimerHideTimerRef.current);
      wagerTimerHideTimerRef.current = null;
    }
    if (wagerTimerFadeTimerRef.current) {
      clearTimeout(wagerTimerFadeTimerRef.current);
      wagerTimerFadeTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearWagerTimerDisplayTimers(), [clearWagerTimerDisplayTimers]);

  useEffect(() => {
    if (phase !== "wager") return;
    clearWagerTimerDisplayTimers();
    wagerTimerFadeScheduledRef.current = false;
    setWagerTimerShown(true);
    setWagerTimerFading(false);
    setWagerTimerFadeStartedAt(0);
  }, [phase, clearWagerTimerDisplayTimers]);

  useEffect(() => {
    if (wagerSecondsLeft > 0 || !wagerTimerShown || wagerTimerFadeScheduledRef.current) return undefined;

    wagerTimerFadeScheduledRef.current = true;
    wagerTimerHideTimerRef.current = setTimeout(() => {
      setWagerTimerFadeStartedAt(Date.now());
      setWagerTimerFading(true);
      wagerTimerFadeTimerRef.current = setTimeout(() => {
        setWagerTimerShown(false);
        setWagerTimerFading(false);
        setWagerTimerFadeStartedAt(0);
      }, CHRONO_FADEOUT_MS);
    }, CHRONO_HIDE_DELAY_MS);

    return () => {
      if (!wagerTimerFadeScheduledRef.current) return;
      clearWagerTimerDisplayTimers();
    };
  }, [wagerSecondsLeft, wagerTimerShown, clearWagerTimerDisplayTimers]);

  useEffect(() => {
    if (!wagerTimerFading) return undefined;
    const iv = setInterval(() => {
      compositeRevRef.current += 1;
    }, 150);
    const stop = setTimeout(() => clearInterval(iv), CHRONO_FADEOUT_MS + 100);
    return () => {
      clearInterval(iv);
      clearTimeout(stop);
    };
  }, [wagerTimerFading]);

  // Précharge les samples
  useEffect(() => {
    if (starting) return undefined;

    void preloadAudioSamples([
      PUSH_AUDIO_SRC,
      TRAP_OPEN_AUDIO_SRC,
      VALID_AUDIO_SRC,
      ALARME_AUDIO_SRC,
      CHRONO_AUDIO_SRC,
    ]);

    const unlock = () => {
      void unlockAudioContext();
    };
    document.addEventListener("pointerdown", unlock);
    document.addEventListener("keydown", unlock);

    return () => {
      document.removeEventListener("pointerdown", unlock);
      document.removeEventListener("keydown", unlock);
    };
  }, [starting]);

  const { stopRecording: stopCompositeRecording } = useCompositeRecorder(streamRef, playScreenRef, {
    enabled: RECORD_COMPOSITE_LIVE && !starting && !!currentQ,
    recordingBitrates: recordingBitrates ?? COMPOSITE_RECORDER_BITRATES,
    compositeRevRef,
    flashWagerRef,
    liveOverlayRef: compositeLiveRef,
  });

  useEffect(() => {
    compositeRevRef.current += 1;
  }, [
    phase,
    visibleAnswerCount,
    wagers,
    bank,
    currentIdx,
    currentQ?.id,
    revealedDoors,
    correctHighlighted,
    flashWagerStart,
    chanceNotice,
    wagerTimerShown,
    wagerTimerFading,
    wagerTimerFadeStartedAt,
  ]);

  const scheduleTimer = useCallback((fn, ms) => {
    const t = setTimeout(fn, ms);
    timersRef.current.push(t);
    return t;
  }, []);

  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  }, []);

  const stopChronoAudio = useCallback(() => {
    stopActiveStoppableSample();
  }, []);

  const playPushSound = useCallback(() => {
    void playGameSample(PUSH_AUDIO_SRC, 1);
  }, []);

  const playTrapOpenSound = useCallback(() => {
    void playGameSample(TRAP_OPEN_AUDIO_SRC, TRAP_OPEN_GAIN);
  }, []);

  const playValidSound = useCallback(() => playGameSampleWithEnd(VALID_AUDIO_SRC), []);

  const endGameRef = useRef(() => {});

  const scheduleTrapFromPending = useCallback(
    (pending, gen) => {
      const { data, wrongOrder } = pending;
      const waves = buildTrapRevealWaves(wrongOrder);
      setPhase("dropping");
      dropTimelineRef.current = {
        startedAt: Date.now(),
        wrongOrder,
        waves,
        correctIndex: data.correct_index,
        dropIntervalMs: DROP_INTERVAL,
        animDurationMs: TRAP_DROP_ANIM_MS,
        highlightAtMs: DROP_INTERVAL * TRAP_REVEAL_WAVE_COUNT,
      };

      const playOpenSounds = (count) => {
        if (count <= 0) return;
        playTrapOpenSound();
        if (count > 1) {
          scheduleTimer(() => {
            if (questionGenerationRef.current !== gen) return;
            playTrapOpenSound();
          }, TRAP_OPEN_SOUND_STAGGER_MS);
        }
      };

      const revealWave = (doorIndices, atMs) => {
        scheduleTimer(() => {
          if (questionGenerationRef.current !== gen) return;
          void unlockAudioContext().then(() => {
            if (questionGenerationRef.current !== gen) return;
            playOpenSounds(doorIndices.length);
            setRevealedDoors((prev) => {
              const next = [...prev];
              for (const doorIdx of doorIndices) {
                if (!next.includes(doorIdx)) next.push(doorIdx);
              }
              return next;
            });
          });
        }, atMs);
      };

      waves.forEach((doorIndices, waveIdx) => {
        revealWave(doorIndices, DROP_INTERVAL * waveIdx);
      });

      const highlightAt = DROP_INTERVAL * TRAP_REVEAL_WAVE_COUNT;
      scheduleTimer(() => {
        if (questionGenerationRef.current !== gen) return;
        setCorrectHighlighted(true);
      }, highlightAt);

      scheduleTimer(() => {
        if (questionGenerationRef.current !== gen) return;
        if (data.is_final) {
          endGameRef.current();
        } else if (data.chance_granted) {
          setChanceNotice(data.chance_granted);
          setPhase("chance-notice");
          scheduleTimer(() => {
            if (questionGenerationRef.current !== gen) return;
            setChanceNotice(null);
            setBank(data.bank_after);
            setCurrentIdx(data.next_question_index);
          }, CHANCE_NOTICE_MS);
        } else {
          setBank(data.bank_after);
          setCurrentIdx(data.next_question_index);
        }
      }, highlightAt + CORRECT_HIGHLIGHT_MS);
    },
    [scheduleTimer, playTrapOpenSound],
  );

  const tryScheduleTrapPrelude = useCallback(() => {
    if (trapSequenceScheduledRef.current) return;
    if (!pendingTrapRef.current) return;
    if (chronoEndedAtRef.current == null) return;

    trapSequenceScheduledRef.current = true;
    const gen = questionGenerationRef.current;
    const elapsed = Date.now() - chronoEndedAtRef.current;
    const delay = Math.max(0, PRE_TRAP_VALID_DELAY_MS - elapsed);

    scheduleTimer(() => {
      if (questionGenerationRef.current !== gen) return;
      void playValidSound().then(() => {
        if (questionGenerationRef.current !== gen) return;
        scheduleTimer(() => {
          if (questionGenerationRef.current !== gen) return;
          void unlockAudioContext().then(() => {
            if (questionGenerationRef.current !== gen) return;
            scheduleTrapFromPending(pendingTrapRef.current, gen);
          });
        }, 250);
      });
    }, delay);
  }, [scheduleTimer, playValidSound, scheduleTrapFromPending]);

  const tryScheduleTrapPreludeRef = useRef(tryScheduleTrapPrelude);
  tryScheduleTrapPreludeRef.current = tryScheduleTrapPrelude;

  // ----- Session bootstrap -----
  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }

    let cancelled = false;

    (async () => {
      const stream = streamRef.current;
      try {
        assertLiveAvTracks(stream);
      } catch (err) {
        stopMediaStream(stream);
        streamRef.current = null;
        toast.error(err?.message || CAMERA_GUARD_MESSAGES.NO_VIDEO);
        navigate("/camera");
        return;
      }

      // Attache temporaire pour valider des frames réelles avant création de session.
      const probe = document.createElement("video");
      probe.muted = true;
      probe.playsInline = true;
      probe.setAttribute("playsinline", "true");
      probe.srcObject = stream;
      try {
        await probe.play().catch(() => {});
        await ensureCameraImageUsable(probe, stream, {
          sampleCount: 4,
          sampleIntervalMs: 140,
          timeoutMs: 3500,
        });
      } catch (err) {
        stopMediaStream(stream);
        streamRef.current = null;
        toast.error(err?.message || CAMERA_GUARD_MESSAGES.NO_VIDEO);
        navigate("/camera");
        return;
      } finally {
        probe.srcObject = null;
      }

      if (cancelled) return;

      try {
        const reloadAttempt = isPageReload();
        const headers = { ...authHeader(token).headers };
        if (reloadAttempt) headers["X-Play-Reload"] = "1";

        const { data } = await api.post("/sessions", {}, { headers });
        if (cancelled) return;
        sessionIdRef.current = data.session_id;
        if (data.recording_bitrates) setRecordingBitrates(data.recording_bitrates);
        setQuestions(data.questions);
        setBank(data.current_bank ?? data.starting_bank);
        setCurrentIdx(data.current_question_index ?? 0);
        setSession(data);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          const detail = err?.response?.data?.detail || "";
          if (
            detail.includes("déjà lancé une session") ||
            detail.includes("Session interrompue") ||
            detail.includes("déjà terminée")
          ) {
            navigate("/thank-you?aborted=1");
            return;
          }
          toast.error(detail || "Accès refusé");
          navigate("/");
          return;
        }
        const detail = err?.response?.data?.detail || "Impossible de démarrer";
        toast.error(detail);
        navigate("/");
        return;
      } finally {
        setStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => stopChronoAudio(), [stopChronoAudio]);

  const showEarlyWagerPopup = useCallback(() => {
    toast.error("Mise impossible avant le chronomètre", { duration: 3000 });
  }, []);

  // ----- Attach webcam stream to PIP once <video> is rendered -----
  useEffect(() => {
    if (starting) return;
    const el = videoRef.current;
    const stream = streamRef.current;
    if (el && stream && el.srcObject !== stream) {
      el.srcObject = stream;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [starting, streamRef]);

  // ----- Phase state machine: drive transitions for each question -----
  useLayoutEffect(() => {
    if (starting || !currentQ) return;

    // Reset AVANT peinture — évite le flash « toutes les réponses visibles »
    clearAllTimers();
    stopChronoAudio();
    questionGenerationRef.current += 1;
    const gen = questionGenerationRef.current;
    wagerLockedRef.current = false;
    trapSequenceScheduledRef.current = false;
    pendingTrapRef.current = null;
    chronoEndedAtRef.current = null;
    clearWagerTimerDisplayTimers();
    wagerTimerFadeScheduledRef.current = false;
    setWagerTimerShown(false);
    setWagerTimerFading(false);
    setWagersLocked(false);
    setPhase("intro-answers");
    setVisibleAnswerCount(0);
    setWagers(INITIAL_WAGERS);
    setCorrectIdx(null);
    setRevealedDoors([]);
    setCorrectHighlighted(false);
    dropTimelineRef.current = null;
    setActiveSlot(null);
    setWagerSecondsLeft(WAGER_SECONDS);

    const anchor = Date.now();
    const scheduleAt = (fn, offsetMs) => {
      const delay = Math.max(0, anchor + offsetMs - Date.now());
      scheduleTimer(() => {
        if (questionGenerationRef.current !== gen) return;
        fn();
      }, delay);
    };

    // 1) Révéler les 4 propositions une par une (1ère après 2s, puis +3s, de gauche à droite)
    for (let i = 1; i <= 4; i += 1) {
      scheduleAt(() => {
        void unlockAudioContext().then(() => playPushSound());
        setVisibleAnswerCount(i);
      }, FIRST_ANSWER_DELAY + ANSWER_REVEAL_INTERVAL * (i - 1));
    }

    // 2) Afficher la question
    const answersDoneAt = FIRST_ANSWER_DELAY + ANSWER_REVEAL_INTERVAL * 3 + QUESTION_REVEAL_DELAY;
    scheduleAt(() => setPhase("intro-question"), answersDoneAt);

    // 3) Phase réflexion
    const thinkingStartAt = answersDoneAt + 800;
    scheduleAt(() => setPhase("thinking"), thinkingStartAt);

    // 4) Phase mises
    const thinkingMs = (currentQ.reveal_time_seconds ?? 45) * 1000;
    scheduleAt(() => setPhase("wager"), thinkingStartAt + thinkingMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, starting, currentQ?.id, playPushSound]);

  // ----- Countdown mises : horloge murale (résiste au changement d'onglet) -----
  useEffect(() => {
    if (phase !== "wager") return undefined;

    const deadline = Date.now() + WAGER_SECONDS * 1000;
    wagerDeadlineRef.current = deadline;
    setWagerSecondsLeft(WAGER_SECONDS);
    setFlashWagerStart(true);
    flashWagerRef.current = { active: true, startedAt: performance.now() };
    const flashTimeout = setTimeout(() => {
      setFlashWagerStart(false);
      flashWagerRef.current = { active: false, startedAt: 0 };
    }, 2400);

    const tick = () => {
      if (!wagerDeadlineRef.current) return;
      const left = Math.max(0, Math.ceil((wagerDeadlineRef.current - Date.now()) / 1000));
      setWagerSecondsLeft(left);
      if (left <= 0 && !wagerLockedRef.current) {
        lockWagerRef.current();
      }
    };

    tick();
    const iv = setInterval(tick, 250);
    const onVisibility = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(iv);
      clearTimeout(flashTimeout);
      document.removeEventListener("visibilitychange", onVisibility);
      flashWagerRef.current = { active: false, startedAt: 0 };
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ----- Musique chrono + alarme via Web Audio (simulation + mix enregistrement) -----
  // Chrono45 : silencieux pour le candidat (paroles audibles), toujours dans la vidéo.
  useEffect(() => {
    if (phase !== "wager") return undefined;

    let cancelled = false;
    chronoEndedAtRef.current = null;

    void unlockAudioContext().then(async () => {
      if (cancelled) return;
      void playGameSample(ALARME_AUDIO_SRC, 1);
      await playGameSampleWithEnd(CHRONO_AUDIO_SRC, 1, { stoppable: true, toSpeakers: false });
      if (cancelled) return;
      chronoEndedAtRef.current = Date.now();
      tryScheduleTrapPreludeRef.current();
    });

    return () => {
      cancelled = true;
    };
  }, [phase]);

  // ----- Wager helpers -----
  const applyWagerChange = (idx, nextValue) => {
    setWagers((prev) => {
      const next = [...prev];
      const tempEmpty = next.filter((w, i) => (i === idx ? nextValue === 0 : w === 0)).length;
      if (nextValue > 0 && tempEmpty < 1) {
        toast.error("Une proposition doit rester vide");
        return prev;
      }
      const newTotal = next.reduce((a, b, i) => a + (i === idx ? nextValue : b), 0);
      if (newTotal > bank) {
        return prev;
      }
      next[idx] = nextValue;
      return next;
    });
  };

  const canWager = phase === "wager" && !submitting && !wagersLocked;
  const isBeforeWagerChrono = phase === "intro-answers" || phase === "intro-question" || phase === "thinking";

  const increment = (idx, step) => {
    if (isBeforeWagerChrono) {
      showEarlyWagerPopup();
      return;
    }
    if (!canWager) return;
    void unlockAudioContext();
    applyWagerChange(idx, Math.max(0, wagers[idx] + step));
  };

  const allIn = (idx) => {
    if (isBeforeWagerChrono) {
      showEarlyWagerPopup();
      return;
    }
    if (!canWager) return;
    void unlockAudioContext();
    const otherTotal = wagers.reduce((a, b, i) => a + (i === idx ? 0 : b), 0);
    applyWagerChange(idx, bank - otherTotal);
  };

  const lockWager = useCallback(async () => {
    if (wagerLockedRef.current || submitting || phase !== "wager") return;
    void unlockAudioContext();
    setSubmitting(true);
    try {
      const payload = wagers;
      const { data } = await api.post(
        `/sessions/${sessionIdRef.current}/answer`,
        { wagers: payload },
        authHeader(token),
      );

      setCorrectIdx(data.correct_index);

      const wrongIndices = [0, 1, 2, 3].filter((i) => i !== data.correct_index);
      const wonOnCorrect = (data.bank_won ?? wagers[data.correct_index]) > 0;
      const wrongOrder = wonOnCorrect
        ? shuffle(wrongIndices)
        : buildWrongRevealOrder(wrongIndices, wagers);

      pendingTrapRef.current = { data, wrongOrder };
      wagerLockedRef.current = true;
      setWagersLocked(true);
      tryScheduleTrapPrelude();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la validation");
      setPhase("wager");
    } finally {
      setSubmitting(false);
    }
  }, [submitting, phase, wagers, token, tryScheduleTrapPrelude]);

  lockWagerRef.current = lockWager;

  const endGame = useCallback(() => {
    void (async () => {
      let compositeBlob = null;
      if (RECORD_COMPOSITE_LIVE) {
        compositeBlob = await stopCompositeRecording();
        if (!compositeBlob?.size) {
          await new Promise((r) => setTimeout(r, 400));
          compositeBlob = await stopCompositeRecording();
        }
      }

      if (compositeBlob?.size) {
        flushSync(() => setCompositeVideoBlob(compositeBlob));
      } else {
        toast.error("Enregistrement vidéo incomplet.");
      }

      navigate("/thank-you", { state: { compositeBlob: compositeBlob?.size ? compositeBlob : null } });
    })();
  }, [navigate, stopCompositeRecording, setCompositeVideoBlob]);

  endGameRef.current = endGame;

  // ----- Derived values -----
  const totalWager = wagers.reduce((a, b) => a + b, 0);
  const remaining = bank - totalWager;

  if (starting) {
    return (
      <div className="min-h-screen bg-md-navy relative flex items-center justify-center">
        <Loader2 className="animate-spin text-md-cyan" size={40} />
      </div>
    );
  }
  if (!currentQ) {
    return (
      <div className="min-h-screen bg-md-navy flex items-center justify-center text-white">
        Chargement...
      </div>
    );
  }

  const showWagerTimer = wagerTimerShown;
  const showFooterTotals = phase === "wager" && (wagerTimerShown || wagerTimerFading);
  const trapWagerDisabled = !canWager && !isBeforeWagerChrono;

  return (
    <div
      ref={playScreenCallbackRef}
      className="play-screen relative bg-md-navy overflow-hidden flex flex-col"
      data-testid="play-screen"
      data-composite-phase={phase}
      data-composite-visible-count={String(visibleAnswerCount)}
      data-composite-question={currentQ?.text ?? ""}
      data-composite-answers={JSON.stringify(currentQ?.answers ?? [])}
      data-composite-wagers={JSON.stringify(wagers)}
      data-composite-total-wager={String(totalWager)}
      data-composite-remaining={String(remaining)}
      data-composite-bank={String(bank)}
      data-composite-wager-seconds={String(wagerSecondsLeft)}
      data-composite-wager-timer-shown={wagerTimerShown ? "1" : "0"}
      data-composite-wager-timer-fading={wagerTimerFading ? "1" : "0"}
      data-composite-wager-timer-fade-at={String(wagerTimerFadeStartedAt || 0)}
      data-composite-chance-notice={chanceNotice || ""}
      data-composite-revealed-doors={JSON.stringify(revealedDoors)}
      data-composite-correct-highlighted={correctHighlighted ? "1" : "0"}
      data-composite-correct-index={String(correctIdx ?? -1)}
    >
      <BackgroundOrbs data-composite-exclude />
      <div className="play-capture-stage relative z-10 flex flex-col flex-1 min-h-0">
      {flashWagerStart && (
        <div
          className="play-composite-flash absolute inset-0 z-[60] pointer-events-none"
          data-composite-exclude
          style={{
            background: "radial-gradient(circle at center, rgba(239,68,68,0.7) 0%, rgba(239,68,68,0.0) 70%)",
            animation: "triFlash 2.4s ease-out forwards",
          }}
          data-testid="wager-start-flash"
        />
      )}
      {/* Top bar — Banque | Question | Chrono (slot fixe toujours réservé) */}
      <div className="play-header relative">
        <div className="play-header-bank flex flex-col justify-center">
          <div className="play-bank-label uppercase tracking-[0.35em] text-white/60 mb-0.5">
            Banque actuelle
          </div>
          <div
            className="play-bank-value font-bold glow-text-white leading-none play-glow-wrap"
            data-testid="current-bank"
          >
            {formatMoney(bank)}
          </div>
        </div>

        <div className="play-header-question">
          <div
            className="play-question-num rounded-lg md:rounded-xl flex items-center justify-center font-bold text-slate-950 md-btn-solid shadow-md-glow-sm"
            data-testid="question-number"
          >
            Q{currentIdx + 1}{questions.length > 0 ? ` / ${questions.length}` : ""}
          </div>

          <div
            className="play-question-card rounded-xl md:rounded-2xl flex items-center justify-center text-center"
            style={{
              background: "linear-gradient(180deg, #f5f7fb 0%, #d8dde8 100%)",
              border: "1px solid rgba(255,255,255,0.4)",
            }}
          >
            {showQuestionText ? (
              <h2
                className="play-question-text font-bold text-slate-900 leading-tight play-question-enter"
                data-testid="question-text"
              >
                {currentQ.text}
              </h2>
            ) : (
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#0B1120]/40">
                Découverte des propositions...
              </div>
            )}
          </div>
        </div>

        <div className="play-header-timer" data-composite-exclude>
          <div
            className={`play-timer-box${
              !showWagerTimer ? " play-timer-box--hidden" : wagerTimerFading ? " play-timer-box--fading" : ""
            }`}
            data-testid="wager-timer"
            aria-hidden={!showWagerTimer}
          >
            <Timer className="text-white shrink-0" size={26} strokeWidth={2.5} />
            <div className="play-timer-digits">
              <div className="play-timer-value font-bold text-white drop-shadow-[0_4px_15px_rgba(0,0,0,0.5)]">
                {String(wagerSecondsLeft).padStart(2, "0")}
              </div>
              <div className="play-timer-unit">sec.</div>
            </div>
          </div>
        </div>
      </div>

      <div className="play-main relative flex-1 min-h-0 flex flex-col">
        <div className="play-traps" key={currentQ.id}>
          {currentQ.answers.map((ans, idx) => {
            const answerVisible = idx < visibleAnswerCount;
            const isDropped = phase === "dropping" && revealedDoors.includes(idx);
            const isWinner = phase === "dropping" && correctHighlighted && idx === correctIdx;

            return (
              <div
                key={`${currentQ.id}-${idx}`}
                className="min-w-0"
                style={{
                  transition: "opacity 0.55s ease, transform 0.55s ease",
                  opacity: answerVisible ? 1 : 0,
                  transform: answerVisible ? "translateY(0)" : "translateY(16px)",
                  pointerEvents: answerVisible ? "auto" : "none",
                }}
                aria-hidden={!answerVisible}
              >
                <TrapDoor
                  index={idx}
                  answer={answerVisible ? ans : undefined}
                  wager={wagers[idx]}
                  isActive={activeSlot === idx && canWager}
                  isWinner={isWinner}
                  isDropped={isDropped}
                  disabled={trapWagerDisabled}
                  beforeChrono={isBeforeWagerChrono}
                  onFocus={() => {
                    if (isBeforeWagerChrono) {
                      showEarlyWagerPopup();
                      return;
                    }
                    setActiveSlot(idx);
                  }}
                  onIncrement={(step) => increment(idx, step)}
                  onAllIn={() => allIn(idx)}
                />
              </div>
            );
          })}
        </div>

        {/* Vignette webcam + totaux — PIP visible candidat, exclu du calque composite (data-composite-exclude) */}
        <div className="play-footer">
          <div className="play-footer-bar">
            <div
              className={`play-footer-side play-footer-side--left fade-up${
                !showFooterTotals ? " invisible" : wagerTimerFading ? " play-footer-side--fading" : ""
              }`}
            >
              <div className="play-footer-label uppercase tracking-[0.3em] text-white/60 mb-0.5">
                Total misé
              </div>
              <div className="play-glow-wrap">
                <div
                  className="play-footer-money font-bold glow-text-white"
                  data-testid="total-wager"
                >
                  {formatMoney(totalWager)}
                </div>
              </div>
            </div>

            <div className="play-footer-center" data-composite-exclude>
              <div className="play-footer-video play-footer-video--pip">
                <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover play-video-mirror" data-testid="webcam-pip" />
                <CandidateSilhouetteGuide />
                <div className="absolute top-1.5 left-1.5 z-10 flex items-center gap-1 text-white text-[9px] uppercase tracking-widest bg-black/60 px-1.5 py-0.5 rounded">
                  <Circle className="fill-[#EF4444] text-[#EF4444]" size={7} />
                  REC
                </div>
              </div>
            </div>

            <div
              className={`play-footer-side play-footer-side--right fade-up${
                !showFooterTotals ? " invisible" : wagerTimerFading ? " play-footer-side--fading" : ""
              }`}
            >
              <div className="play-footer-label uppercase tracking-[0.3em] text-white/60 mb-0.5">
                Restant
              </div>
              <div className="play-glow-wrap">
                <div
                  className="play-footer-money font-bold accent-text"
                  data-testid="remaining-bank"
                >
                  {formatMoney(remaining)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Chance overlay */}
      {phase === "chance-notice" && chanceNotice && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center backdrop-blur-xl bg-md-navy/80"
          data-testid="chance-notice"
        >
          <div className="text-center fade-up px-6">
            <div className="text-xs uppercase tracking-[0.4em] text-white/50 mb-4">
              {chanceNotice === "last_chance" ? "Attention" : "Pas de panique"}
            </div>
            <h1 className="font-black uppercase text-5xl md:text-8xl leading-none">
              {chanceNotice === "last_chance" ? (
                <span className="text-red-400">Dernière chance</span>
              ) : (
                <span className="text-md-cyan">Nouvelle chance</span>
              )}
            </h1>
            <p className="mt-6 text-lg md:text-2xl text-white/80 max-w-xl mx-auto">
              {chanceNotice === "last_chance"
                ? "On vous remet 100 000 € pour la dernière question. Tout se joue ici."
                : "On vous remet 100 000 € pour la question suivante. Profitez-en."}
            </p>
            <div className="mt-8 money-stack text-2xl">100 000 €</div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Play;
